import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as ClientsActions from './clients.actions';
import { ClientInterfaceWithId } from '@sk/sk-interfaces';


export const CLIENTS_FEATURE_KEY = 'clients';

export interface ClientsState extends EntityState<ClientInterfaceWithId> {
  selectedClientId: string | null; // which Clients record has been selected
  clientsLoaded: boolean; // has the Clients list been loaded
  clientsLoadError?: string | null; // last none error (if any)

  queryClientError?: string | null; // last none error when adding a new client (if any)

  addError?: string | null; // last none error when adding a new client (if any)
  addClientLoaded: boolean; // last none error when adding a new client (if any)
}

export interface ClientsPartialState {
  readonly [CLIENTS_FEATURE_KEY]: ClientsState;
}

export const clientsAdapter: EntityAdapter<ClientInterfaceWithId> = createEntityAdapter<ClientInterfaceWithId>({
  selectId: selectClientId,
  sortComparer: sortByName
});

export const initialState: ClientsState = clientsAdapter.getInitialState({
  // set initial required properties
  selectedClientId: null,
  clientsLoaded: false,
  addClientLoaded: false
});


export function selectClientId(a: ClientInterfaceWithId): string {
  //In this case this would be optional since primary key is id
  return a.id;
}


export function sortByName(a: ClientInterfaceWithId, b: ClientInterfaceWithId): number {
  if (a.name && b.name) {
    return a.name.localeCompare(b.name);
  }
}

const clientsReducer = createReducer(
  initialState,

  on(ClientsActions.loadClients, (state) => {
    if (state.clientsLoadError) {
      delete state.clientsLoadError;
    }
    if (state.addError) {
      delete state.addError;
    }
    return ({
      ...state,
      loaded: false
    });
  }),

  on(ClientsActions.loadClientsSuccess, (state, { clients }) => {
    if (state.clientsLoadError) {
      delete state.clientsLoadError;
    }
    return clientsAdapter.setAll(clients, { ...state, loaded: true });
  }),

  on(ClientsActions.loadClientsFailure, (state, { error }) => {
    return ({
      ...state,
      clientsLoadError: error
    });
  }),

  // ACTIONS INITIATED FROM THIS CLIENT
  // FIREBASE WILL AUTOMATICALLY UPDATE STATE THROUGH QUERY!!
  on(ClientsActions.addClient, (state, { client }) => {
    if (state.addError) {
      delete state.addError;
    }
    return ({
      ...state,
      addClientLoaded: false
    });
  }),


  on(ClientsActions.addError, (state, { error }) => {
    return ({
      ...state,
      addError: error.message
    });
  }),

  on(ClientsActions.addSuccess, (state, {}) => {
    if (state.addError) {
      delete state.addError;
    }
    return ({
      ...state,
      addClientLoaded: true
    });
  }),


  on(ClientsActions.removeClient, (state, {
      id
    }) =>
      clientsAdapter.removeOne(id, {
        completed: false,
        ...state
      })
  ),
  on(ClientsActions.updateClient, (state, { id, changes }) =>
    clientsAdapter.updateOne({
        id,
        changes
      },
      state)
  ),

  on(ClientsActions.updateError, (state, { error }) => {
    return ({
      ...state
    });
  }),

  /**
   * ACTION FROM FireStore
   */
  on(ClientsActions.fromFireStoreAddClient, (state, { client }) => {
    if (state.queryClientError) {
      delete state.queryClientError;
    }
    return clientsAdapter.setOne(client, state);
  }),

  on(ClientsActions.fromFireStoreModifyClient, (state, { client }) => {
    if (state.queryClientError) {
      delete state.queryClientError;
    }
    return clientsAdapter.updateOne({
        id: client.id,
        changes: client
      },
      state);
  }),
  on(ClientsActions.fromFireStoreRemoveClient, (state, {
    id
  }) => {
    if (state.queryClientError) {
      delete state.queryClientError;
    }
    return clientsAdapter.removeOne(id, state);
  }),
  on(ClientsActions.queryFireStoreClientsError, (state, {
    error
  }) => {
    return {
      queryClientError: error.message,
      ...state
    };
  })
);


export function reducer(state: ClientsState | undefined, action: Action) {
  return clientsReducer(state, action);
}
